import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Spinner from '../ui/Spinner';
import Alert from '../ui/Alert';
import Button from '../ui/Button';
import jsQR from 'jsqr';
import { logDebitoAsync } from '../../lib/debugLog';

interface QRCodeScannerProps {
  onScan: (data: string) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
  isActive?: boolean;
  title?: string;
  description?: string;
}

const QRCodeScanner: React.FC<QRCodeScannerProps> = ({
  onScan,
  onError,
  onClose,
  isActive = true,
  title = "Escaneie o QR Code",
  description = "Posicione o QR Code dentro da área de leitura"
}) => {
  const [scanResult, setScanResult] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [isLoadingCamera, setIsLoadingCamera] = useState<boolean>(true);
  const [cameraPermission, setCameraPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const [currentFacingMode, setCurrentFacingMode] = useState<'environment' | 'user'>('environment');

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Detectar se é dispositivo móvel
  const isMobileDevice = useCallback((): boolean => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }, []);

  // **PARAR CÂMERA ATUAL - VERSÃO SIMPLES**
  const stopCamera = useCallback(() => {

    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsScanning(false);
    setIsLoadingCamera(false);
  }, []);

  const cleanup = useCallback(() => {
    stopCamera();
    setScanResult(null);
    setScanError(null);
  }, [stopCamera]);

  // **FUNÇÃO DE PROCESSAMENTO DO SCAN - CORRIGIDA E SIMPLIFICADA**
  const processarScan = useCallback((data: string) => {
    logDebitoAsync('scanner.qr_lido', {
      tamanhoPayload: data.length,
      payload: data.slice(0, 500),
    });

    try {

      const parsedData = JSON.parse(data);

      // Verificar se é um QR Code de débito Tucoins. QR de cobrança (com `c`) também
      // traz os campos antigos, para o app antigo; aqui ele segue intacto para a
      // tela tratar como cobrança.
      if (parsedData.t === 'tucoins_debito' && !parsedData.c) {

        // Processar débito - criar objeto estruturado COMPATÍVEL
        const transacaoDebito = {
          paymentIntentId: parsedData.id,
          valor: parseFloat(parsedData.v),
          numeroComanda: parsedData.cmd || '',
          operadorId: parsedData.op,
          unidadeId: parsedData.un,
          timestamp: parsedData.ts,
          tipo_operacao: 'debito' 
        };

        
        logDebitoAsync('scanner.qr_debito_reconhecido', {
          paymentIntentId: transacaoDebito.paymentIntentId,
          valorBruto: parsedData.v,
          valorParseado: transacaoDebito.valor,
          unidadeId: transacaoDebito.unidadeId,
          operadorId: transacaoDebito.operadorId,
        });

        // Chamar onScan com os dados processados
        onScan(JSON.stringify(transacaoDebito));
      } else {
        logDebitoAsync('scanner.qr_nao_e_debito', { tipo: parsedData?.t ?? null });
        // Outros tipos de QR Code - passar dados originais
        onScan(data);
      }
    } catch (error) {
      logDebitoAsync('scanner.qr_nao_e_json', { erro: error });
      // Se não for JSON, processar como texto normal
      onScan(data);
    }
  }, [onScan]);

  const handleScanError = useCallback((error: Error) => {
    console.error('❌ Erro na câmera:', error);
    logDebitoAsync('scanner.erro_camera', { erro: error, nome: error.name });

    let errorMessage = 'Não foi possível acessar a câmera.';

    switch (error.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        errorMessage = 'Permissão da câmera negada.';
        setCameraPermission('denied');
        break;
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        errorMessage = 'Nenhuma câmera encontrada.';
        break;
      case 'NotSupportedError':
        errorMessage = 'Navegador não suporta câmera.';
        break;
      case 'NotReadableError':
      case 'TrackStartError':
        errorMessage = 'Câmera em uso por outra aplicação.';
        break;
      default:
        errorMessage = `Erro: ${error.message || 'Tente novamente.'}`;
    }

    setScanError(errorMessage);
    stopCamera();

    if (onError) {
      onError(error);
    }
  }, [onError, stopCamera]);

  // **INICIAR CÂMERA - VERSÃO SIMPLES E FUNCIONAL**
  const startCamera = useCallback(async () => {
    try {
      stopCamera();

      setIsLoadingCamera(true);
      setScanError(null);

      // Configurações simples - tentar câmera traseira primeiro
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: currentFacingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().then(() => {
            setIsLoadingCamera(false);
            setIsScanning(true);
            setCameraPermission('granted');
          }).catch(error => {
            console.error('❌ Erro ao reproduzir vídeo:', error);
            handleScanError(error);
          });
        };

        videoRef.current.onerror = (error) => {
          console.error('❌ Erro no elemento de vídeo:', error);
          handleScanError(new Error('Erro no elemento de vídeo'));
        };
      }

      return true;
    } catch (error: any) {
      console.error('❌ Erro ao iniciar câmera:', error);

      // Se falhou com câmera traseira, tentar frontal
      if (currentFacingMode === 'environment') {
        setCurrentFacingMode('user');
        return false; // Vai tentar novamente no próximo efeito
      }

      handleScanError(error);
      return false;
    }
  }, [currentFacingMode, handleScanError, stopCamera]);

  // **DETECÇÃO DE QR CODE - ATUALIZADA PARA USAR processarScan**
  const detectQRCode = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !isScanning || scanResult) return;

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (!context || video.videoWidth === 0 || video.videoHeight === 0) return;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });

      if (code) {
        setScanResult(code.data);
        stopCamera();
        
        // **AQUI ESTÁ A CORREÇÃO: usar processarScan em vez de onScan direto**
        processarScan(code.data);
      }
    } catch (error) {
      console.error('❌ Erro na detecção QR:', error);
    }
  }, [isScanning, scanResult, stopCamera, processarScan]);

  // **EFEITO PARA SCAN CONTÍNUO**
  useEffect(() => {
    if (isScanning && !scanResult) {
      scanIntervalRef.current = setInterval(detectQRCode, 200);
    } else {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
    }

    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
    };
  }, [isScanning, scanResult, detectQRCode]);

  // **EFEITO PRINCIPAL - INICIAR CÂMERA**
  useEffect(() => {
    if (isActive) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      if (!isActive) {
        stopCamera();
      }
    };
  }, [isActive, startCamera, stopCamera]);

  // **EFEITO PARA TENTAR NOVA CÂMERA QUANDO facingMode MUDAR**
  useEffect(() => {
    if (isActive && currentFacingMode) {
      const timer = setTimeout(() => {
        startCamera();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isActive, currentFacingMode, startCamera]);

  // **EFEITO DE DESMONTAGEM**
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const handleRetry = useCallback(async () => {
    cleanup();
    setCurrentFacingMode('environment'); // Reset para traseira
    await new Promise(resolve => setTimeout(resolve, 500));
    startCamera();
  }, [cleanup, startCamera]);

  const switchCamera = useCallback(async () => {
    const newFacingMode = currentFacingMode === 'environment' ? 'user' : 'environment';
    setCurrentFacingMode(newFacingMode);
  }, [currentFacingMode]);

  const handleClose = useCallback(() => {
    cleanup();
    if (onClose) {
      onClose();
    }
  }, [cleanup, onClose]);

  // Componente de overlay de permissão negada
  const renderPermissionDenied = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 flex flex-col items-center justify-center bg-black z-10 p-2 text-center"
    >
      <div className="text-red-400 text-4xl mb-4">📷🚫</div>
      <Alert
        type="danger"
        message="Acesso à câmera bloqueado"
        description="Para escanear QR Codes, você precisa permitir o acesso à câmera."
      />
      <Button onClick={handleRetry} variant="primary" className="mt-6">
        Permitir Câmera e Tentar Novamente
      </Button>
    </motion.div>
  );

  // Componente de overlay de erro genérico
  const renderErrorOverlay = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 flex flex-col items-center justify-center bg-black z-10 p-4 text-center"
    >
      <Alert type="danger" message={scanError || 'Erro desconhecido'} />
      <Button onClick={handleRetry} variant="primary" className="mt-4">
        Tentar Novamente
      </Button>
    </motion.div>
  );

  // **BOTÃO PARA ALTERNAR CÂMERAS**
  const renderCameraSwitchButton = () => {
    const isMobile = isMobileDevice();

    if (!isMobile || scanResult || scanError || !isScanning) {
      return null;
    }

    return (
      <Button
        onClick={switchCamera}
        variant="secondary"
        size="sm"
        className="absolute top-3 right-3 z-20 bg-black/70 backdrop-blur-sm border-white/30 hover:bg-black/90"
        disabled={isLoadingCamera}
      >
        <span className="flex items-center gap-2">
          <span className="text-lg">
            {currentFacingMode === 'environment' ? '📷' : '🤳'}
          </span>
          {currentFacingMode === 'environment' ? 'Traseira' : 'Frontal'}
          {isLoadingCamera && <Spinner size="sm" />}
        </span>
      </Button>
    );
  };

  const getCameraInfo = () => {
    return currentFacingMode === 'environment' ? 'Câmera Traseira' : 'Câmera Frontal';
  };

  return (
    <div className="bg-background rounded-lg shadow-lg border border-primary/20 max-w-md mx-auto p-4 md:p-6">
      <div className="text-center mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
          {title}
        </h2>
        {description && (
          <p className="text-sm text-white/60">
            {description}
          </p>
        )}
        <div className="text-xs text-white/40 mt-1">
          📷 {isMobileDevice() ? 'Dispositivo Móvel' : 'Desktop'} -
          <span className="text-green-400"> {getCameraInfo()}</span>
        </div>
      </div>

      <div className="relative bg-black rounded-lg overflow-hidden mb-4 md:mb-6 aspect-square border-2 border-primary/20">
        {/* Elemento de vídeo para a câmera */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />

        {/* Canvas para processamento de QR Code (oculto) */}
        <canvas ref={canvasRef} className="hidden" />

        <AnimatePresence>
          {/* Loading */}
          {isLoadingCamera && !scanError && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col items-center justify-center bg-black z-10"
            >
              <Spinner size="lg" />
              <p className="mt-3 text-white text-sm">Iniciando câmera...</p>
              <p className="text-white/60 text-xs mt-1">
                {getCameraInfo()}
              </p>
            </motion.div>
          )}

          {/* Permissão Negada */}
          {cameraPermission === 'denied' && (
            renderPermissionDenied()
          )}

          {/* Outros Erros */}
          {scanError && cameraPermission !== 'denied' && (
            renderErrorOverlay()
          )}

          {/* Scanner Ativo */}
          {isScanning && !scanResult && !scanError && cameraPermission !== 'denied' && (
            <>
              {renderCameraSwitchButton()}
              <div className="absolute inset-8 border-2 border-green-400 rounded-lg pointer-events-none animate-pulse" />
              <div className="absolute top-3 left-3 right-20 text-center text-white text-xs bg-black/70 rounded px-3 py-2 backdrop-blur-sm">
                📷 Centralize o QR Code na área de leitura
              </div>
            </>
          )}

          {/* Sucesso */}
          {scanResult && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col items-center justify-center bg-green-500/20 z-10 p-6 text-center"
            >
              <div className="text-green-400 text-4xl mb-4">✅</div>
              <Alert
                type="success"
                message="QR Code identificado!"
                description="Processando informações..."
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        {(scanError || cameraPermission === 'denied') && (
          <Button
            onClick={handleRetry}
            variant="secondary"
            disabled={isLoadingCamera}
            className="flex-1"
          >
            {cameraPermission === 'denied' ? 'Solicitar Permissão' : 'Tentar Novamente'}
          </Button>
        )}

        {onClose && (
          <Button
            onClick={handleClose}
            variant="danger"
            className="flex-1 bg-gradient-to-r from-[#c5a663] via-[#c7b66b] to-[#c5a663] "
          >
            Fechar Scanner
          </Button>
        )}

        {/* Botão de alternar câmera também na parte inferior para facilidade */}
        {isMobileDevice() && !scanResult && !scanError && (
          <Button
            onClick={switchCamera}
            variant="secondary"
            className="flex-1"
            disabled={isLoadingCamera}
          >
            <span className="flex items-center justify-center gap-2">
              <span className="text-lg">
                {currentFacingMode === 'environment' ? '📷' : '🤳'}
              </span>
              Alternar para {currentFacingMode === 'environment' ? 'Frontal' : 'Traseira'}
              {isLoadingCamera && <Spinner size="sm" />}
            </span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default QRCodeScanner;